import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import isAfter from 'date-fns/isAfter';

import { getRouteUrl } from '@utils/urls';
import { isChicoreeApp } from '@utils/environmentCheck';

import HTMLRenderer from '@common/components/CMSComponents/HTMLRenderer';
import { PriceText } from '@common/components/PriceText';
import { HOME, CMS_LOOKBOOK_PAGE } from '@common/routes.mjs';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import CMSComponentsRenderer from '@common/components/CMSComponents/CMSComponentsRenderer';
import PageHeroMedia from '@common/components/PageHeroMedia';
import { PARAGRAPH_BLOCK_TYPE } from '@common/constants/cms';
import DeepLinks from '@common/components/DeepLinks';
import { videoShape } from '@common/types/cms';
import { VARIANT_ERROR } from '@common/constants/alertBox';
import { explicitNull, or } from '@common/tempAirbnbPropTypesReplacement';

import {
  DescriptionContent,
  DescriptionWrapper,
  Lead,
  StyledAlertBox,
  Subtitle,
  Title,
} from './Lookbook.styled';

export function Lookbook({
  pageData: {
    title,
    lead,
    body,
    image,
    meta,
    video,
    videoDesktop,
    productsExpiryDate,
    subtitle,
  },
}) {
  const { t } = useTranslation();
  const {
    query: { slug },
    locale,
    push,
  } = useRouter();
  const isExpired = isAfter(new Date(), new Date(productsExpiryDate));
  const isAppEnvironemnt = isChicoreeApp();

  const metaTitle = t(
    'lookbooks.meta.title',
    '{{title}} | Neue Fashioninspiration',
    {
      title: meta.metaTitle,
    },
  );
  const metaDescription = t(
    'lookbooks.meta.description',
    'Lass dich von unserem neuen Lookbook {{title}} zu neuen Fashion-Styles inspirieren. Jetzt durchstöbern.',
    { title: meta.metaTitle },
  );

  const pageUrl = getRouteUrl(locale, CMS_LOOKBOOK_PAGE, { slug });

  const [paragraphBlock, bodyWithoutParagraph] = (body || []).reduce(
    (acc, cmsBlock) => {
      acc[cmsBlock.type === PARAGRAPH_BLOCK_TYPE ? 0 : 1].push(cmsBlock);

      return acc;
    },
    [[], []],
  );

  return (
    <>
      <DeepLinks />
      <Layout
        description={metaDescription}
        ogImage={image?.url}
        pageType="lookbook"
        title={metaTitle}
        canonicalPageUrl={pageUrl}
        noNavbar={isAppEnvironemnt}
        noFooter={isAppEnvironemnt}
      >
        <RouteLayout
          backRoute={HOME}
          backRouteAs={getRouteUrl(locale, HOME)}
          columns={12}
          stickyTitle={title}
          isHero={!!image}
          withoutStickyBar={isAppEnvironemnt}
        >
          <PageHeroMedia
            image={{ src: image?.url, alt: image?.altText || '' }}
            video={video}
            videoDesktop={videoDesktop}
          />
          <DescriptionWrapper>
            <DescriptionContent>
              <Title>
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
                <PriceText>{title}</PriceText>
              </Title>
              {lead && (
                <Lead>
                  <HTMLRenderer
                    customAs="span"
                    html={lead}
                    nl2br
                  />
                </Lead>
              )}
              {!!paragraphBlock.length && (
                <CMSComponentsRenderer body={paragraphBlock} />
              )}
              {isExpired && (
                <StyledAlertBox
                  buttonText={t(
                    'lookbooks.alertExpired.buttonTitle',
                    'Neuheiten entdecken',
                  )}
                  title={t(
                    'lookbooks.alertExpired.title',
                    'Produkte ausverkauft',
                  )}
                  description={t(
                    'lookbooks.alertExpired.text',
                    'Die nachfolgenden Produkte führen wir nicht mehr in unserem Sortiment. Aktuelle Inspirationen findest Du auf unserer Startseite.',
                  )}
                  variant={VARIANT_ERROR}
                  onButtonClick={() => push(HOME, getRouteUrl(locale, HOME))}
                  buttonId="lookbook-expired-button"
                />
              )}
            </DescriptionContent>
          </DescriptionWrapper>
          <CMSComponentsRenderer body={bodyWithoutParagraph} />
        </RouteLayout>
      </Layout>
    </>
  );
}

Lookbook.propTypes = {
  pageData: PropTypes.shape({
    body: or([PropTypes.arrayOf(PropTypes.shape({})), explicitNull()]),
    image: PropTypes.shape({
      altText: PropTypes.string,
      url: PropTypes.string,
    }),
    video: videoShape,
    videoDesktop: videoShape,
    lead: PropTypes.string,
    meta: PropTypes.shape({
      metaTitle: PropTypes.string.isRequired,
    }).isRequired,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
    productsExpiryDate: PropTypes.string.isRequired,
  }).isRequired,
};

export default Lookbook;
